import { useState, useEffect } from 'react'
import {
  getKillSignals,
  type KillSignal,
  type KillSignalStatus,
  type KillSignalsResponse,
  type SignalFormat,
  type StaleInitiative,
  type TargetStatus,
} from '../services/api'

const STALENESS_SIGNAL_ID = 'initiative-staleness'

// Status is announced as text in the badge; colour is reinforcement only.
const STATUS_LABEL: Record<KillSignalStatus, string> = {
  'green': 'On track',
  'amber': 'Amber',
  'tripped': 'Tripped',
  'no-data': 'No data',
  'expired': 'Expired',
}

const STATUS_CLASS: Record<KillSignalStatus, string> = {
  'green': 'badge-signal-green',
  'amber': 'badge-signal-amber',
  'tripped': 'badge-signal-tripped',
  'no-data': 'badge-signal-nodata',
  'expired': 'badge-signal-expired',
}

// Targets carry no format of their own; these are the known metric keys.
const TARGET_FORMAT: Record<string, SignalFormat> = {
  ms_deals: 'count',
  ps_deals: 'count',
  new_mrr: 'currency',
  ps_revenue: 'currency',
}

function formatNumber(value: number | null): string {
  if (value === null || value === undefined) {
    return '—'
  }
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/0$/, '')
}

function formatValue(value: number | null, format: SignalFormat | undefined): string {
  if (value === null || value === undefined) {
    return '—'
  }
  switch (format) {
    case 'pct':
      return `${formatNumber(value)}%`
    case 'currency':
      return `$${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}`
    default:
      return formatNumber(value)
  }
}

function formatDeadline(deadline: string): string {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const due = new Date(`${deadline}T00:00:00`)
  const days = Math.round((due.getTime() - today.getTime()) / 86400000)

  if (days === 0) return `${deadline} (today)`
  if (days > 0) return `${deadline} (${days} day${days === 1 ? '' : 's'} left)`
  return `${deadline} (${-days} day${days === -1 ? '' : 's'} overdue)`
}

function percentOfTarget(target: TargetStatus): string {
  if (!target.target_min) return '—'
  return `${Math.round((target.actual / target.target_min) * 100)}%`
}

function formatTargetRange(target: TargetStatus): string {
  const format = TARGET_FORMAT[target.metric]
  if (target.target_max === null || target.target_max === undefined) {
    return `${formatValue(target.target_min, format)}+`
  }
  return `${formatValue(target.target_min, format)}–${formatValue(target.target_max, format)}`
}

function SignalChip({ signal }: { signal: KillSignal }) {
  const statement = signal.details?.statement || signal.signal_id
  const owner = signal.details?.owner
  const format = signal.details?.format

  return (
    <li className={`signal-chip signal-chip-${signal.status}`}>
      <div className="signal-chip-header">
        <span className={`badge ${STATUS_CLASS[signal.status]}`}>{STATUS_LABEL[signal.status]}</span>
        {owner && <span className="signal-owner">{owner}</span>}
      </div>
      <p className="signal-statement">{statement}</p>
      <dl className="signal-meta">
        <div className="signal-meta-row">
          <dt>Value</dt>
          <dd>
            {formatValue(signal.value, format)} <span className="signal-meta-sep">vs</span>{' '}
            {formatValue(signal.threshold, format)}
          </dd>
        </div>
        <div className="signal-meta-row">
          <dt>Deadline</dt>
          <dd>{signal.deadline ? formatDeadline(signal.deadline) : '—'}</dd>
        </div>
      </dl>
    </li>
  )
}

function TargetsTable({ targets }: { targets: TargetStatus[] }) {
  if (targets.length === 0) {
    return <p className="board-empty">No targets evaluated yet.</p>
  }

  return (
    <table className="admin-table board-table">
      <caption className="board-table-caption">Quarter {targets[0].quarter}</caption>
      <thead>
        <tr>
          <th scope="col">Metric</th>
          <th scope="col">Target</th>
          <th scope="col">Actual</th>
          <th scope="col">% of target</th>
        </tr>
      </thead>
      <tbody>
        {targets.map((target) => (
          <tr key={target.metric}>
            <td>{target.metric}</td>
            <td>{formatTargetRange(target)}</td>
            <td>{formatValue(target.actual, TARGET_FORMAT[target.metric])}</td>
            <td>{percentOfTarget(target)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function StalenessStrip({ stale }: { stale: StaleInitiative[] }) {
  if (stale.length === 0) {
    return <p className="board-empty">No stale initiatives.</p>
  }

  return (
    <ul className="staleness-strip">
      {stale.map((item) => (
        <li key={item.id} className="stale-item">
          <span className="stale-item-id">{item.id}</span>
          <span className="stale-item-days">{item.days_stale} days stale</span>
        </li>
      ))}
    </ul>
  )
}

function KillSignalBoard() {
  const [data, setData] = useState<KillSignalsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    async function fetchBoard() {
      setIsLoading(true)
      setError(null)
      try {
        setData(await getKillSignals())
      } catch (err) {
        setError('Failed to load kill signals. Please try again.')
        console.error('Failed to get kill signals:', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchBoard()
  }, [])

  if (error) {
    return <div className="error">{error}</div>
  }
  if (isLoading) {
    return <div className="loading">Loading kill signals...</div>
  }
  if (!data) {
    return null
  }

  const active = data.signals.filter((s) => s.status !== 'expired')
  const expired = data.signals.filter((s) => s.status === 'expired')
  const stale = data.signals.find((s) => s.signal_id === STALENESS_SIGNAL_ID)?.details?.stale ?? []

  return (
    <div className="kill-signal-board">
      <section className="board-section">
        <h2 className="board-section-title">Active signals</h2>
        {active.length === 0 ? (
          <p className="board-empty">No active signals.</p>
        ) : (
          <ul className="signal-grid">
            {active.map((signal) => (
              <SignalChip key={signal.signal_id} signal={signal} />
            ))}
          </ul>
        )}
      </section>

      {expired.length > 0 && (
        <section className="board-section">
          <h2 className="board-section-title">Past deadline</h2>
          <ul className="signal-grid">
            {expired.map((signal) => (
              <SignalChip key={signal.signal_id} signal={signal} />
            ))}
          </ul>
        </section>
      )}

      <section className="board-section">
        <h2 className="board-section-title">Targets vs actuals</h2>
        <TargetsTable targets={data.targets} />
      </section>

      <section className="board-section">
        <h2 className="board-section-title">Initiative staleness</h2>
        <StalenessStrip stale={stale} />
      </section>

      <p className="board-generated">
        Evaluated {new Date(data.generated_at).toLocaleString()}
      </p>
    </div>
  )
}

export default KillSignalBoard
